import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { AuthTenantOption, LoginResponse } from 'shared-models/browser';
import { authService } from '../../services/auth/auth.service';
import classes from './auth-provider.module.scss';

type SessionUser = LoginResponse['user'];

interface AuthContextType {
  isAuthenticated: boolean;
  login: (authPayload: LoginResponse) => Promise<void>;
  logout: () => Promise<void>;
  user: SessionUser | null;
  availableTenants: AuthTenantOption[];
  refreshAvailableTenants: () => Promise<AuthTenantOption[]>;
  selectedTenantId: string | null;
  setSelectedTenantId: (tenantId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [availableTenants, setAvailableTenants] = useState<AuthTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Summary: Persists active tenant selection from a pre-fetched tenant list so tenant-scoped routes and API calls stay aligned across refre...
  // Details: docs/function-library/web/contexts-pages.md#contexts-authprovider
  const applyTenantSelection = (profile: SessionUser, tenantOptions: AuthTenantOption[]) => {
    setAvailableTenants(tenantOptions);

    const savedTenantId = localStorage.getItem('selectedTenantId');
    const canUseSaved = savedTenantId
      ? tenantOptions.some((tenant) => tenant.tenantId === savedTenantId)
      : false;

    const fallbackTenant = profile.tenantId ?? tenantOptions[0]?.tenantId ?? null;
    const nextSelectedTenant = canUseSaved ? savedTenantId! : fallbackTenant;

    setSelectedTenantIdState(nextSelectedTenant);
    if (nextSelectedTenant) {
      localStorage.setItem('selectedTenantId', nextSelectedTenant);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
  };

  // Summary: Fetches tenant options for an already-known profile when callers don't have a parallel list at hand (e.g.
  // Details: docs/function-library/web/contexts-pages.md#contexts-authprovider
  const hydrateTenantSelection = async (profile: SessionUser) => {
    const tenantOptions = await authService.tenants();
    applyTenantSelection(profile, tenantOptions);
  };

  useEffect(() => {
    // Summary: Rehydrates session by validating server-side auth cookie against backend auth/me AND fetching tenant options in parallel before...
    // Details: docs/function-library/web/contexts-pages.md#contexts-authprovider
    const bootstrapSession = async () => {
      const [profileResult, tenantOptionsResult] = await Promise.allSettled([
        authService.me(),
        authService.tenants(),
      ]);

      if (profileResult.status === 'rejected') {
        setIsAuthenticated(false);
        setUser(null);
        setAvailableTenants([]);
        setSelectedTenantIdState(null);
        localStorage.removeItem('authUser');
        localStorage.removeItem('selectedTenantId');
        setLoading(false);
        return;
      }

      const profile = profileResult.value;
      const tenantOptions = tenantOptionsResult.status === 'fulfilled' ? tenantOptionsResult.value : [];
      setIsAuthenticated(true);
      setUser(profile);
      localStorage.setItem('authUser', JSON.stringify(profile));
      applyTenantSelection(profile, tenantOptions);
      setLoading(false);
    };

    bootstrapSession();
  }, []);

  const login = async (authPayload: LoginResponse) => {
    setIsAuthenticated(true);
    setUser(authPayload.user);
    localStorage.setItem('authUser', JSON.stringify(authPayload.user));
    await hydrateTenantSelection(authPayload.user);
  };

  const setSelectedTenantId = useCallback((tenantId: string) => {
    setSelectedTenantIdState(tenantId);
    localStorage.setItem('selectedTenantId', tenantId);
  }, []);

  // Summary: Reconciles the tenant access snapshot without reloading the active application.
  // Used by the maintenance gate after a backgrounded tab resumes, because an SSE event can be
  // missed while the browser has suspended its connection.
  const refreshAvailableTenants = useCallback(async (): Promise<AuthTenantOption[]> => {
    const tenantOptions = await authService.tenants();
    setAvailableTenants(tenantOptions);
    setSelectedTenantIdState((current) => {
      if (current && tenantOptions.some((tenant) => tenant.tenantId === current)) {
        return current;
      }
      const next = user?.tenantId ?? tenantOptions[0]?.tenantId ?? null;
      if (next) {
        localStorage.setItem('selectedTenantId', next);
      } else {
        localStorage.removeItem('selectedTenantId');
      }
      return next;
    });
    return tenantOptions;
  }, [user?.tenantId]);

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Keep local state cleanup even if backend logout fails so UI cannot stay in an authenticated state.
    }

    setIsAuthenticated(false);
    setUser(null);
    setAvailableTenants([]);
    setSelectedTenantIdState(null);
    localStorage.removeItem('authUser');
    localStorage.removeItem('selectedTenantId');
  };

  if (loading) {
    return (
      <div className={classes.loadingScreen} role="status" aria-live="polite" dir="rtl" data-testid="loader">
        <span>טוען...</span>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        user,
        availableTenants,
        refreshAvailableTenants,
        selectedTenantId,
        setSelectedTenantId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

